import {engineContext} from './context.js';

(function(g){'use strict';
const VERSION='0.1.0-alpha';

const ALIASES=Object.freeze({
  revenue:['RevenueFromContractWithCustomerExcludingAssessedTax','Revenues','SalesRevenueNet'],
  netIncome:['NetIncomeLoss','ProfitLoss'],
  operatingIncome:['OperatingIncomeLoss'],
  operatingCashFlow:['NetCashProvidedByUsedInOperatingActivities'],
  capex:['PaymentsToAcquirePropertyPlantAndEquipment'],
  dilutedShares:['WeightedAverageNumberOfDilutedSharesOutstanding'],
  dilutedEPS:['EarningsPerShareDiluted']
});

function finite(v){const n=Number(v);return v!==null&&v!==undefined&&v!==''&&Number.isFinite(n)?n:null;}
function chooseFact(facts,names){
  for(const name of names){
    if(Array.isArray(facts?.[name])&&facts[name].length) return {name,rows:facts[name]};
  }
  return null;
}
function annualRows(rows){
  return (rows||[]).filter(r=>['10-K','10-K/A','20-F','20-F/A','40-F','40-F/A'].includes(r.form)||r.fp==='FY');
}
function durationDays(r){
  if(!r.start||!r.end) return null;
  const a=Date.parse(r.start), b=Date.parse(r.end);
  return Number.isFinite(a)&&Number.isFinite(b)?Math.round((b-a)/86400000):null;
}
function isAnnualDuration(r){
  const d=durationDays(r);
  return d===null || (d>=300 && d<=430);
}
function fiscalKey(r){
  if(r.fy!==null&&r.fy!==undefined&&String(r.fy)!=='') return String(r.fy);
  return r.end?String(r.end).slice(0,4):null;
}
function better(a,b){
  const rank=x=>x?.form==='10-K'||x?.form==='20-F'||x?.form==='40-F'?3:
    x?.form==='10-K/A'||x?.form==='20-F/A'||x?.form==='40-F/A'?2:1;
  if(rank(a)!==rank(b)) return rank(a)>rank(b)?a:b;
  if(String(a?.filed||'')!==String(b?.filed||'')) return String(a?.filed||'')>String(b?.filed||'')?a:b;
  return String(a?.end||'')>=String(b?.end||'')?a:b;
}
function seriesFor(facts,key){
  const found=chooseFact(facts,ALIASES[key]||[]);
  if(!found) return {sourceFact:null,points:[]};
  const byFY=new Map();
  for(const r of annualRows(found.rows).filter(isAnnualDuration)){
    const fy=fiscalKey(r); if(!fy) continue;
    const v=finite(r.value); if(v===null) continue;
    byFY.set(fy,byFY.has(fy)?better(byFY.get(fy),r):r);
  }
  const points=[...byFY.entries()].map(([fy,r])=>({
    fy:Number(fy),value:finite(r.value),start:r.start||null,end:r.end||null,filed:r.filed||null,
    form:r.form||null,accession:r.accession||null,unit:r.unit||null,sourceFact:found.name
  })).sort((a,b)=>a.fy-b.fy);
  return {sourceFact:found.name,points};
}
function yoy(points){
  return points.map((p,i)=>{
    const prev=i?points[i-1]:null;
    const growth=prev&&prev.value!==0?((p.value-prev.value)/Math.abs(prev.value))*100:null;
    return {...p,yoyPct:growth};
  });
}
function cagr(points){
  if(!points||points.length<2) return null;
  const first=points[0],last=points[points.length-1],years=last.fy-first.fy;
  if(years<=0||first.value<=0||last.value<=0) return null;
  return (Math.pow(last.value/first.value,1/years)-1)*100;
}
function build(companyFacts={},opts={}){
  const facts=companyFacts.facts||{};
  const maxYears=Math.max(2,Math.min(20,Number(opts.maxYears)||10));
  const metrics={};
  for(const key of Object.keys(ALIASES)){
    const s=seriesFor(facts,key);
    s.points=s.points.slice(-maxYears);
    s.points=yoy(s.points);
    s.cagrPct=cagr(s.points);
    metrics[key]=s;
  }

  const fcf=[];
  const ocf=new Map(metrics.operatingCashFlow.points.map(x=>[x.fy,x]));
  const capex=new Map(metrics.capex.points.map(x=>[x.fy,x]));
  for(const fy of [...ocf.keys()].filter(x=>capex.has(x)).sort((a,b)=>a-b)){
    const a=ocf.get(fy),b=capex.get(fy);
    if(a.unit&&b.unit&&a.unit!==b.unit) continue;
    fcf.push({fy,value:a.value-b.value,unit:a.unit||b.unit||null,
      derivedFrom:['operatingCashFlow','capex'],filed:[a.filed,b.filed].filter(Boolean).sort().pop()||null});
  }
  const fcfSeries=yoy(fcf);

  return Object.freeze({
    version:VERSION,symbol:companyFacts.symbol||null,entityName:companyFacts.entityName||null,
    maxYears,metrics:Object.freeze(metrics),
    derived:Object.freeze({freeCashFlow:Object.freeze({points:Object.freeze(fcfSeries),cagrPct:cagr(fcfSeries)})}),
    note:'Historical SEC series only; trend thresholds and investment scoring remain uncalibrated.'
  });
}

function selfTest(){
  const t=[],ok=(n,p)=>t.push({name:n,pass:!!p});
  const annual=(value,fy,filed,form='10-K',unit='USD')=>({value,fy,start:`${fy}-01-01`,end:`${fy}-12-31`,filed,form,fp:'FY',unit,accession:`A${fy}`});
  const q=(value,fy)=>({value,fy,start:`${fy}-01-01`,end:`${fy}-03-31`,filed:`${fy}-05-01`,form:'10-Q',fp:'Q1',unit:'USD'});
  const cf={symbol:'AAA',entityName:'AAA Corp',facts:{
    Revenues:[annual(100,2023,'2024-02-01'),annual(120,2024,'2025-02-01'),annual(150,2025,'2026-02-01'),q(50,2026)],
    NetIncomeLoss:[annual(10,2023,'2024-02-01'),annual(12,2024,'2025-02-01'),annual(18,2025,'2026-02-01')],
    NetCashProvidedByUsedInOperatingActivities:[annual(20,2023,'2024-02-01'),annual(25,2024,'2025-02-01'),annual(30,2025,'2026-02-01')],
    PaymentsToAcquirePropertyPlantAndEquipment:[annual(5,2023,'2024-02-01'),annual(6,2024,'2025-02-01'),annual(8,2025,'2026-02-01')],
    WeightedAverageNumberOfDilutedSharesOutstanding:[
      annual(1000,2024,'2025-02-01','10-K','shares'),
      annual(1100,2025,'2026-02-01','10-K','shares')
    ]
  }};
  const h=build(cf);
  ok('symbol preserved',h.symbol==='AAA');
  ok('entity preserved',h.entityName==='AAA Corp');
  ok('annual revenue has three years',h.metrics.revenue.points.length===3);
  ok('quarterly row excluded',!h.metrics.revenue.points.some(x=>x.fy===2026));
  ok('revenue alias retained',h.metrics.revenue.sourceFact==='Revenues');
  ok('revenue 2025 value correct',h.metrics.revenue.points.at(-1).value===150);
  ok('revenue YoY correct',h.metrics.revenue.points.at(-1).yoyPct===25);
  ok('revenue CAGR calculated',Math.abs(h.metrics.revenue.cagrPct-22.474487139158896)<1e-9);
  ok('net income history parsed',h.metrics.netIncome.points.length===3);
  ok('OCF history parsed',h.metrics.operatingCashFlow.points.length===3);
  ok('capex history parsed',h.metrics.capex.points.length===3);
  ok('diluted shares unit retained',h.metrics.dilutedShares.points[0].unit==='shares');
  ok('FCF derived for matching years',h.derived.freeCashFlow.points.length===3);
  ok('FCF 2025 correct',h.derived.freeCashFlow.points.at(-1).value===22);
  ok('FCF provenance says derived',h.derived.freeCashFlow.points[0].derivedFrom.length===2);

  const amended={facts:{Revenues:[
    annual(100,2025,'2026-02-01','10-K'),
    annual(101,2025,'2026-03-01','10-K/A')
  ]}};
  const a=build(amended);
  ok('plain 10-K preferred over amendment by policy',a.metrics.revenue.points[0].value===100);

  const limited=build(cf,{maxYears:2});
  ok('maxYears truncates series',limited.metrics.revenue.points.length===2);
  ok('maxYears preserves newest years',limited.metrics.revenue.points[0].fy===2024);

  const empty=build({symbol:'X',facts:{}});
  ok('missing series remains empty',empty.metrics.revenue.points.length===0);
  ok('missing CAGR remains null',empty.metrics.revenue.cagrPct===null);
  ok('missing FCF remains empty',empty.derived.freeCashFlow.points.length===0);

  const badDuration={facts:{Revenues:[{value:999,fy:2025,start:'2025-01-01',end:'2025-03-31',filed:'2026-02-01',form:'10-K',fp:'FY',unit:'USD'}]}};
  ok('short duration annual-looking fact rejected',build(badDuration).metrics.revenue.points.length===0);

  const mixedUnits={facts:{
    NetCashProvidedByUsedInOperatingActivities:[annual(30,2025,'2026-02-01','10-K','USD')],
    PaymentsToAcquirePropertyPlantAndEquipment:[annual(8,2025,'2026-02-01','10-K','EUR')]
  }};
  ok('FCF not derived across incompatible units',build(mixedUnits).derived.freeCashFlow.points.length===0);

  const api={VERSION,build,seriesFor,yoy,cagr};
  ok('no trade signal',!('tradeSignal' in api));
  ok('no capital permission',!('capitalPermission' in api));
  ok('no universal score',!('score' in api));

  return Object.freeze({passed:t.filter(x=>x.pass).length,total:t.length,tests:Object.freeze(t)});
}
g.ZooInvestSECFinancialHistoryEngine=Object.freeze({VERSION,ALIASES,seriesFor,yoy,cagr,build,selfTest});
})(engineContext);